import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Users, X, Key, CheckSquare, Square, Download, Search, Edit2, AlertTriangle, AlertCircle, RefreshCw, Power, Lock, Route, GripVertical, ChevronUp, ChevronDown, Calendar, RotateCcw } from 'lucide-react';

const DataManagement = () => {
  // Password protection state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  const [activeTab, setActiveTab] = useState('inspectors');
  const [inspectors, setInspectors] = useState([]);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showManpowerModal, setShowManpowerModal] = useState(false);
  const [selectedPark, setSelectedPark] = useState(null);
  const [manpowerList, setManpowerList] = useState([]);
  const [newEmployee, setNewEmployee] = useState({ employee_id: '', employee_name: '', category: 'Gardener' });
  
  // Edit employee state
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editEmployeeData, setEditEmployeeData] = useState({ employee_name: '', category: 'Gardener' });
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editData, setEditData] = useState({});
  
  // Bulk assignment state
  const [selectedParkIds, setSelectedParkIds] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignSupervisor, setBulkAssignSupervisor] = useState('');
  
  // Password reset state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [newPin, setNewPin] = useState('');
  
  // Inspector assigned parks modal
  const [showInspectorParksModal, setShowInspectorParksModal] = useState(false);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [inspectorParks, setInspectorParks] = useState([]);
  const [reassigningSupervisor, setReassigningSupervisor] = useState('');
  const [selectedReassignParks, setSelectedReassignParks] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  
  // Visit Order Combinations state
  const [showVisitOrderModal, setShowVisitOrderModal] = useState(false);
  const [visitOrderInspector, setVisitOrderInspector] = useState(null);
  const [visitOrderParks, setVisitOrderParks] = useState([]);
  const [visitCombinations, setVisitCombinations] = useState([]);
  const [activeCombinationIndex, setActiveCombinationIndex] = useState(0);
  const [savingVisitOrder, setSavingVisitOrder] = useState(false);
  const [loadingVisitOrder, setLoadingVisitOrder] = useState(false);
  const [visitOrderEnforced, setVisitOrderEnforced] = useState(false);
  const [masterVisitOrderEnabled, setMasterVisitOrderEnabled] = useState(true);
  const [savingMasterToggle, setSavingMasterToggle] = useState(false);
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await adminAPI.verifyProtectedPassword(password);
      setIsAuthenticated(true);
      toast.success('Access granted');
    } catch (error) {
      toast.error('Invalid password');
      setPassword('');
    } finally {
      setVerifying(false);
    }
  };

  const [formData, setFormData] = useState({
    supervisor_id: '',
    supervisor_name: '',
    pin: '',
    park_id: '',
    park_name: '',
    zone: '',
    lat: '',
    lng: '',
    required_manpower: 0,
    assigned_inspector_id: ''
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
      return;
    }
    if (isAuthenticated) {
      fetchData();
    }
  }, [user, userType, navigate, activeTab, authLoading, isAuthenticated]);

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      // Always fetch inspectors for the assignment dropdown
      const inspectorsResponse = await adminAPI.getInspectors();
      setInspectors(inspectorsResponse.data.inspectors);
      
      // Always fetch parks (needed for assigned parks count in inspectors tab)
      const parksResponse = await adminAPI.getParks();
      setParks(parksResponse.data.parks);
      
      // Fetch master visit order toggle
      try {
        const masterToggleResponse = await adminAPI.getMasterVisitOrderToggle();
        setMasterVisitOrderEnabled(masterToggleResponse.data.enabled);
      } catch (e) {
        setMasterVisitOrderEnabled(true); // Default to enabled
      }
      
      if (activeTab === 'parks') {
        setSelectedParkIds([]); // Reset selection when refreshing
      }
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (activeTab === 'inspectors') {
        await adminAPI.createInspector({
          supervisor_id: formData.supervisor_id,
          supervisor_name: formData.supervisor_name,
          pin: formData.pin,
          assigned_parks: []
        });
        toast.success('Inspector created successfully');
      } else if (activeTab === 'parks') {
        const parkData = {
          park_id: formData.park_id,
          park_name: formData.park_name,
          zone: formData.zone,
          division: formData.division || '',
          required_manpower: parseInt(formData.required_manpower) || 0,
          manpower_list: [],
          assigned_inspector_id: formData.assigned_inspector_id
        };
        
        // Only add gps_coordinates if lat and lng are provided
        if (formData.lat && formData.lng) {
          parkData.gps_coordinates = {
            lat: parseFloat(formData.lat),
            lng: parseFloat(formData.lng)
          };
        }
        
        await adminAPI.createPark(parkData);
        toast.success('Park created successfully');
      }
      
      setShowForm(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    
    setLoading(true);
    try {
      if (activeTab === 'inspectors') {
        await adminAPI.deleteInspector(id);
        toast.success('Inspector deleted');
      } else if (activeTab === 'parks') {
        await adminAPI.deletePark(id);
        toast.success('Park deleted');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  // Manpower Management Functions
  const openManpowerModal = (park) => {
    setSelectedPark(park);
    setManpowerList(park.manpower_list || []);
    setNewEmployee({ employee_id: '', employee_name: '' });
    setShowManpowerModal(true);
  };

  const addEmployee = () => {
    if (!newEmployee.employee_id.trim() || !newEmployee.employee_name.trim()) {
      toast.error('Please enter both Employee ID and Name');
      return;
    }
    
    // Check for duplicate ID
    if (manpowerList.some(e => e.employee_id === newEmployee.employee_id)) {
      toast.error('Employee ID already exists');
      return;
    }
    
    setManpowerList([...manpowerList, { ...newEmployee }]);
    setNewEmployee({ employee_id: '', employee_name: '', category: 'Gardener' });
  };

  const removeEmployee = (employeeId) => {
    setManpowerList(manpowerList.filter(e => e.employee_id !== employeeId));
  };

  const startEditEmployee = (employee) => {
    setEditingEmployee(employee.employee_id);
    setEditEmployeeData({
      employee_name: employee.employee_name,
      category: employee.category || 'Gardener'
    });
  };

  const cancelEditEmployee = () => {
    setEditingEmployee(null);
    setEditEmployeeData({ employee_name: '', category: 'Gardener' });
  };

  const saveEditEmployee = () => {
    if (!editEmployeeData.employee_name.trim()) {
      toast.error('Employee name cannot be empty');
      return;
    }
    
    setManpowerList(manpowerList.map(emp => 
      emp.employee_id === editingEmployee
        ? { ...emp, employee_name: editEmployeeData.employee_name, category: editEmployeeData.category }
        : emp
    ));
    setEditingEmployee(null);
    setEditEmployeeData({ employee_name: '', category: 'Gardener' });
  };

  const saveManpower = async () => {
    setLoading(true);
    try {
      await adminAPI.updateParkEmployees(selectedPark.park_id, manpowerList);
      toast.success('Manpower list updated successfully');
      setShowManpowerModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update manpower list');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Park Assignment Functions
  const toggleParkSelection = (parkId) => {
    setSelectedParkIds(prev => 
      prev.includes(parkId) 
        ? prev.filter(id => id !== parkId)
        : [...prev, parkId]
    );
  };

  const selectAllParks = () => {
    if (selectedParkIds.length === parks.length) {
      setSelectedParkIds([]);
    } else {
      setSelectedParkIds(parks.map(p => p.park_id));
    }
  };

  const openBulkAssignModal = () => {
    if (selectedParkIds.length === 0) {
      toast.error('Please select at least one park');
      return;
    }
    setBulkAssignSupervisor('');
    setShowBulkAssignModal(true);
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignSupervisor) {
      toast.error('Please select a supervisor');
      return;
    }
    
    setLoading(true);
    try {
      const response = await adminAPI.bulkAssignParks(selectedParkIds, bulkAssignSupervisor);
      toast.success(response.data.message);
      setShowBulkAssignModal(false);
      setSelectedParkIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign parks');
    } finally {
      setLoading(false);
    }
  };

  // Inspector Parks Modal Functions
  const openInspectorParksModal = (inspector) => {
    setSelectedInspector(inspector);
    const assignedParks = parks.filter(p => 
      p.assigned_inspector_id === inspector.supervisor_id || 
      p.assigned_supervisor_id === inspector.supervisor_id
    );
    setInspectorParks(assignedParks);
    setSelectedReassignParks([]);
    setReassigningSupervisor('');
    setShowInspectorParksModal(true);
  };

  const toggleReassignParkSelection = (parkId) => {
    setSelectedReassignParks(prev => 
      prev.includes(parkId) 
        ? prev.filter(id => id !== parkId)
        : [...prev, parkId]
    );
  };

  const handleReassignParks = async () => {
    if (selectedReassignParks.length === 0) {
      toast.error('Please select at least one park');
      return;
    }
    if (!reassigningSupervisor) {
      toast.error('Please select a supervisor to reassign to');
      return;
    }
    
    setReassigning(true);
    try {
      const response = await adminAPI.bulkAssignParks(selectedReassignParks, reassigningSupervisor);
      toast.success(response.data.message);
      
      // Refresh data
      await fetchData();
      
      // Update the modal list
      const updatedParks = inspectorParks.filter(p => !selectedReassignParks.includes(p.park_id));
      setInspectorParks(updatedParks);
      setSelectedReassignParks([]);
      setReassigningSupervisor('');
      
      if (updatedParks.length === 0) {
        setShowInspectorParksModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign parks');
    } finally {
      setReassigning(false);
    }
  };

  // Password Reset Functions
  const openResetPasswordModal = (supervisor) => {
    setSelectedSupervisor(supervisor);
    setNewPin('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!newPin || newPin.length < 4) {
      toast.error('PIN must be at least 4 characters');
      return;
    }
    
    setLoading(true);
    try {
      await adminAPI.resetSupervisorPassword(selectedSupervisor.supervisor_id, newPin);
      toast.success(`Password reset for ${selectedSupervisor.supervisor_name}`);
      setShowResetPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    let rows = [];
    let filename = '';
    
    if (activeTab === 'inspectors') {
      filename = 'Supervisors_List';
      rows.push(['Supervisor ID', 'Supervisor Name', 'Assigned Parks Count']);
      inspectors.forEach(i => {
        const assignedCount = parks.filter(p => 
          p.assigned_inspector_id === i.supervisor_id || 
          p.assigned_supervisor_id === i.supervisor_id
        ).length;
        rows.push([i.supervisor_id, i.supervisor_name, assignedCount]);
      });
    } else if (activeTab === 'parks') {
      filename = 'Parks_List';
      rows.push(['Park ID', 'Park Name', 'Zone', 'Division', 'Supervisor', 'Manpower Count', 'Employees']);
      parks.forEach(p => {
        const employees = p.manpower_list?.map(e => e.employee_name).join('; ') || '-';
        rows.push([p.park_id, p.park_name, p.zone, p.division, p.assigned_inspector_id || p.assigned_supervisor_id || '-', p.manpower_list?.length || 0, employees]);
      });
    }
    
    if (rows.length <= 1) {
      toast.error('No data to download');
      return;
    }
    
    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded successfully');
  };

  // Edit functions
  const openEditModal = (item, type) => {
    setEditItem({ ...item, type });
    if (type === 'supervisor') {
      setEditData({ supervisor_name: item.supervisor_name });
    } else {
      setEditData({
        park_name: item.park_name,
        zone: item.zone,
        division: item.division,
        assigned_inspector_id: item.assigned_inspector_id || '',
        gps_lat: item.gps_coordinates?.lat || '',
        gps_lng: item.gps_coordinates?.lng || '',
        google_maps_link: item.google_maps_link || ''
      });
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      if (editItem.type === 'supervisor') {
        await adminAPI.updateSupervisor(editItem.supervisor_id, editData);
        toast.success('Supervisor updated');
      } else {
        // Prepare data with GPS coordinates
        const updateData = {
          park_name: editData.park_name,
          zone: editData.zone,
          division: editData.division,
          assigned_inspector_id: editData.assigned_inspector_id,
          google_maps_link: editData.google_maps_link
        };
        
        // Add GPS coordinates if provided
        if (editData.gps_lat && editData.gps_lng) {
          updateData.gps_coordinates = {
            lat: parseFloat(editData.gps_lat),
            lng: parseFloat(editData.gps_lng)
          };
        }
        
        await adminAPI.updatePark(editItem.park_id, updateData);
        toast.success('Park updated');
      }
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  // Toggle park active status (renovation)
  const handleToggleParkStatus = async (parkId) => {
    try {
      const response = await adminAPI.toggleParkStatus(parkId);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle park status');
    }
  };

  // Visit Order Combination Functions
  const openVisitOrderModal = async (inspector) => {
    setVisitOrderInspector(inspector);
    setShowVisitOrderModal(true);
    setLoadingVisitOrder(true);
    
    try {
      // IMPORTANT: Fetch fresh parks data to get newly assigned parks
      const parksResponse = await adminAPI.getParks();
      const freshParks = parksResponse.data.parks;
      setParks(freshParks);  // Update the main parks state too
      
      // Get parks assigned to this inspector (using fresh data)
      const assignedParks = freshParks.filter(p => 
        p.assigned_inspector_id === inspector.supervisor_id || 
        p.assigned_supervisor_id === inspector.supervisor_id
      );
      setVisitOrderParks(assignedParks);
      
      // Fetch existing combinations from backend
      const response = await adminAPI.getVisitCombinations(inspector.supervisor_id);
      if (response.data.combinations && response.data.combinations.length > 0) {
        setVisitCombinations(response.data.combinations);
        setActiveCombinationIndex(response.data.active_combination_index || 0);
      } else {
        // Default: create one combination with all parks in current order
        const defaultCombo = assignedParks.map(p => p.park_id);
        setVisitCombinations([defaultCombo]);
        setActiveCombinationIndex(0);
      }
      // Load enforcement setting
      setVisitOrderEnforced(response.data.visit_order_enforced || false);
    } catch (error) {
      console.error('Error loading visit combinations:', error);
      // Default fallback
      const assignedParks = parks.filter(p => 
        p.assigned_inspector_id === inspector.supervisor_id || 
        p.assigned_supervisor_id === inspector.supervisor_id
      );
      setVisitOrderParks(assignedParks);
      const defaultCombo = assignedParks.map(p => p.park_id);
      setVisitCombinations([defaultCombo]);
      setActiveCombinationIndex(0);
      setVisitOrderEnforced(false);
    } finally {
      setLoadingVisitOrder(false);
    }
  };

  const addNewCombination = () => {
    if (visitCombinations.length >= 4) {
      toast.error('Maximum 4 combinations allowed');
      return;
    }
    // Add a new combination with same parks but reversed order
    const lastCombo = visitCombinations[visitCombinations.length - 1] || [];
    const newCombo = [...lastCombo].reverse();
    setVisitCombinations([...visitCombinations, newCombo]);
  };

  const removeCombination = (index) => {
    if (visitCombinations.length <= 1) {
      toast.error('At least one combination is required');
      return;
    }
    const updated = visitCombinations.filter((_, i) => i !== index);
    setVisitCombinations(updated);
    if (activeCombinationIndex >= updated.length) {
      setActiveCombinationIndex(updated.length - 1);
    }
  };

  const moveParkInCombination = (comboIndex, parkIndex, direction) => {
    const combo = [...visitCombinations[comboIndex]];
    const newIndex = direction === 'up' ? parkIndex - 1 : parkIndex + 1;
    
    if (newIndex < 0 || newIndex >= combo.length) return;
    
    [combo[parkIndex], combo[newIndex]] = [combo[newIndex], combo[parkIndex]];
    
    const updatedCombinations = [...visitCombinations];
    updatedCombinations[comboIndex] = combo;
    setVisitCombinations(updatedCombinations);
  };

  const saveVisitCombinations = async () => {
    setSavingVisitOrder(true);
    try {
      await adminAPI.saveVisitCombinations(visitOrderInspector.supervisor_id, {
        combinations: visitCombinations,
        active_combination_index: activeCombinationIndex,
        visit_order_enforced: visitOrderEnforced
      });
      toast.success('Visit order combinations saved');
      setShowVisitOrderModal(false);
    } catch (error) {
      toast.error('Failed to save visit order combinations');
    } finally {
      setSavingVisitOrder(false);
    }
  };

  const setManualOverride = async (comboIndex) => {
    try {
      await adminAPI.setVisitOrderOverride(visitOrderInspector.supervisor_id, comboIndex);
      setActiveCombinationIndex(comboIndex);
      toast.success(`Combination ${comboIndex + 1} set as active for today`);
    } catch (error) {
      toast.error('Failed to set override');
    }
  };

  const getParkName = (parkId) => {
    const park = visitOrderParks.find(p => p.park_id === parkId);
    return park ? park.park_name : parkId;
  };

  // Filter data based on search
  const filteredInspectors = inspectors.filter(i =>
    i.supervisor_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.supervisor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredParks = parks.filter(p =>
    p.park_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.park_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.zone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.division?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.assigned_inspector_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.assigned_supervisor_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Password Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden">
          <div className="bg-gradient-to-r from-slate-700 to-slate-900 text-white p-6">
            <div className="flex items-center gap-3">
              <Lock className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Data Management</h1>
                <p className="text-slate-300 text-sm">Protected Area</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Enter Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Access password"
                className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg focus:border-slate-600 focus:ring-0"
                data-testid="data-management-password-input"
                autoFocus
              />
            </div>
            
            <button
              type="submit"
              disabled={!password || verifying}
              className="w-full h-12 bg-slate-800 hover:bg-slate-900 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center justify-center gap-2"
              data-testid="data-management-password-submit"
            >
              {verifying ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Access Data Management
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="w-full h-10 text-slate-600 hover:text-slate-800 font-semibold"
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:opacity-80">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Data Management
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setActiveTab('inspectors'); setShowForm(false); }}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'inspectors'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Inspectors
          </button>
          <button
            onClick={() => { setActiveTab('parks'); setShowForm(false); }}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'parks'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Parks
          </button>
        </div>

        <div className="mb-4 flex gap-4 flex-wrap items-center">
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-12 pl-10 pr-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
            />
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-12 px-6 bg-slate-600 text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-slate-700 disabled:opacity-50"
            data-testid="refresh-data-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="h-12 px-6 bg-blue-600 text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Add New
          </button>
          <button
            onClick={downloadExcel}
            className="h-12 px-6 bg-green-600 text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-green-700"
          >
            <Download className="w-5 h-5" />
            Download
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">
              {activeTab === 'inspectors' ? 'New Inspector' : 'New Park'}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeTab === 'inspectors' ? (
                <>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Supervisor ID</label>
                    <input
                      type="text"
                      value={formData.supervisor_id || ''}
                      onChange={(e) => setFormData({...formData, supervisor_id: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Inspector Name</label>
                    <input
                      type="text"
                      value={formData.supervisor_name || ''}
                      onChange={(e) => setFormData({...formData, supervisor_name: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">PIN</label>
                    <input
                      type="password"
                      value={formData.pin || ''}
                      onChange={(e) => setFormData({...formData, pin: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Park ID</label>
                    <input
                      type="text"
                      value={formData.park_id || ''}
                      onChange={(e) => setFormData({...formData, park_id: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Park Name</label>
                    <input
                      type="text"
                      value={formData.park_name || ''}
                      onChange={(e) => setFormData({...formData, park_name: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Zone</label>
                    <input
                      type="text"
                      value={formData.zone || ''}
                      onChange={(e) => setFormData({...formData, zone: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Division</label>
                    <input
                      type="text"
                      value={formData.division || ''}
                      onChange={(e) => setFormData({...formData, division: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Latitude (optional)</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.lat || ''}
                      onChange={(e) => setFormData({...formData, lat: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Longitude (optional)</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.lng || ''}
                      onChange={(e) => setFormData({...formData, lng: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Required Manpower</label>
                    <input
                      type="number"
                      value={formData.required_manpower || 0}
                      onChange={(e) => setFormData({...formData, required_manpower: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-slate-700 mb-2 block">Assigned Supervisor</label>
                    <select
                      value={formData.assigned_inspector_id || ''}
                      onChange={(e) => setFormData({...formData, assigned_inspector_id: e.target.value})}
                      className="h-12 px-4 w-full rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                      required
                    >
                      <option value="">Select Supervisor</option>
                      {inspectors.map(insp => (
                        <option key={insp.supervisor_id} value={insp.supervisor_id}>
                          {insp.supervisor_id} - {insp.supervisor_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="md:col-span-2 flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="h-12 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              {activeTab === 'inspectors' ? (
                <>
                  {/* Master Visit Order Toggle */}
                  <div className={`mb-4 p-4 rounded-lg border-2 flex items-center justify-between ${
                    masterVisitOrderEnabled ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <Route className={`w-6 h-6 ${masterVisitOrderEnabled ? 'text-green-600' : 'text-orange-600'}`} />
                      <div>
                        <h4 className="font-bold text-slate-800">Visit Order Enforcement (All Supervisors)</h4>
                        <p className="text-sm text-slate-600">
                          {masterVisitOrderEnabled 
                            ? 'Supervisors with enabled enforcement must follow their assigned visit order' 
                            : 'All supervisors can visit parks in any order (enforcement disabled globally)'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold ${masterVisitOrderEnabled ? 'text-green-700' : 'text-orange-700'}`}>
                        {masterVisitOrderEnabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                      <button
                        onClick={async () => {
                          setSavingMasterToggle(true);
                          try {
                            await adminAPI.setMasterVisitOrderToggle(!masterVisitOrderEnabled);
                            setMasterVisitOrderEnabled(!masterVisitOrderEnabled);
                            toast.success(`Visit order enforcement ${!masterVisitOrderEnabled ? 'enabled' : 'disabled'} for all supervisors`);
                          } catch (error) {
                            toast.error('Failed to update master toggle');
                          } finally {
                            setSavingMasterToggle(false);
                          }
                        }}
                        disabled={savingMasterToggle}
                        className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          masterVisitOrderEnabled 
                            ? 'bg-green-600 focus:ring-green-500' 
                            : 'bg-orange-400 focus:ring-orange-500'
                        } ${savingMasterToggle ? 'opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                            masterVisitOrderEnabled ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  
                  {/* Summary showing unassigned parks */}
                  {(() => {
                    const unassignedParks = parks.filter(p => !p.assigned_inspector_id && !p.assigned_supervisor_id);
                    const totalAssigned = parks.length - unassignedParks.length;
                    return (
                      <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${unassignedParks.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                        <div>
                          <span className="font-bold">Total Parks: {parks.length}</span>
                          <span className="mx-3">|</span>
                          <span className="font-bold text-green-700">Assigned: {totalAssigned}</span>
                          {unassignedParks.length > 0 && (
                            <>
                              <span className="mx-3">|</span>
                              <span className="font-bold text-red-600">Unassigned: {unassignedParks.length}</span>
                            </>
                          )}
                        </div>
                        {unassignedParks.length === 0 && (
                          <span className="text-green-600 font-semibold">✓ All parks assigned</span>
                        )}
                      </div>
                    );
                  })()}
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 font-bold">ID</th>
                        <th className="text-left p-3 font-bold">Name</th>
                        <th className="text-center p-3 font-bold">Assigned Parks</th>
                        <th className="text-center p-3 font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInspectors.map((inspector) => {
                        const assignedCount = parks.filter(p => 
                          p.assigned_inspector_id === inspector.supervisor_id || 
                          p.assigned_supervisor_id === inspector.supervisor_id
                      ).length;
                      return (
                        <tr key={inspector.supervisor_id} className="border-t hover:bg-slate-50">
                          <td className="p-3 font-mono">{inspector.supervisor_id}</td>
                          <td className="p-3 font-medium">{inspector.supervisor_name}</td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => openInspectorParksModal(inspector)}
                              className={`inline-flex px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:ring-2 transition-all ${
                                assignedCount > 0 ? 'bg-blue-100 text-blue-700 hover:ring-blue-400' : 'bg-slate-100 text-slate-500 hover:ring-slate-400'
                              }`}
                              data-testid={`view-parks-${inspector.supervisor_id}`}
                            >
                              {assignedCount} parks
                            </button>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openVisitOrderModal(inspector)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Visit Order Combinations"
                                data-testid={`visit-order-${inspector.supervisor_id}`}
                              >
                                <Route className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => openEditModal(inspector, 'supervisor')}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Edit Supervisor"
                              >
                                <Edit2 className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => openResetPasswordModal(inspector)}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                                title="Reset Password"
                                data-testid={`reset-password-${inspector.supervisor_id}`}
                              >
                                <Key className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(inspector.supervisor_id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Delete Supervisor"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </>
              ) : (
                <>
                  {/* Bulk Assignment Controls */}
                  <div className="mb-4 flex items-center gap-4 flex-wrap">
                    <button
                      onClick={selectAllParks}
                      className="h-10 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg font-semibold text-sm flex items-center gap-2"
                    >
                      {selectedParkIds.length === parks.length ? (
                        <><CheckSquare className="w-4 h-4" /> Deselect All</>
                      ) : (
                        <><Square className="w-4 h-4" /> Select All</>
                      )}
                    </button>
                    
                    {selectedParkIds.length > 0 && (
                      <button
                        onClick={openBulkAssignModal}
                        className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm flex items-center gap-2"
                        data-testid="bulk-assign-btn"
                      >
                        <Users className="w-4 h-4" />
                        Assign {selectedParkIds.length} Parks to Supervisor
                      </button>
                    )}
                    
                    <span className="text-sm text-slate-500">
                      {selectedParkIds.length} of {parks.length} selected
                    </span>
                  </div>
                  
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-center p-3 w-12">
                          <input
                            type="checkbox"
                            checked={selectedParkIds.length === parks.length && parks.length > 0}
                            onChange={selectAllParks}
                            className="h-5 w-5 rounded border-2 border-slate-400 text-blue-600"
                          />
                        </th>
                        <th className="text-left p-3 font-bold">ID</th>
                        <th className="text-left p-3 font-bold">Name</th>
                        <th className="text-left p-3 font-bold">Zone</th>
                        <th className="text-left p-3 font-bold">Division</th>
                        <th className="text-left p-3 font-bold">Supervisor</th>
                        <th className="text-center p-3 font-bold">Manpower</th>
                        <th className="text-center p-3 font-bold">Status</th>
                        <th className="text-center p-3 font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParks.map((park) => (
                        <tr 
                          key={park.park_id} 
                          className={`border-t hover:bg-slate-50 ${selectedParkIds.includes(park.park_id) ? 'bg-blue-50' : ''} ${park.is_active === false ? 'opacity-60' : ''}`}
                        >
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedParkIds.includes(park.park_id)}
                              onChange={() => toggleParkSelection(park.park_id)}
                              className="h-5 w-5 rounded border-2 border-slate-400 text-blue-600"
                              data-testid={`select-park-${park.park_id}`}
                            />
                          </td>
                          <td className="p-3 font-mono">{park.park_id}</td>
                        <td className="p-3 font-medium">{park.park_name}</td>
                        <td className="p-3">{park.zone}</td>
                        <td className="p-3">{park.division}</td>
                        <td className="p-3">{park.assigned_inspector_id || park.assigned_supervisor_id}</td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                            (park.manpower_list?.length || 0) > 0 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            <Users className="w-4 h-4" />
                            {park.manpower_list?.length || 0}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                            park.is_active !== false
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-orange-500 text-white'
                          }`}>
                            {park.is_active !== false ? 'Active' : 'RENOVATION'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => openEditModal(park, 'park')}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Edit Park"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openManpowerModal(park)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Edit Manpower"
                              data-testid={`edit-manpower-${park.park_id}`}
                            >
                              <Users className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleParkStatus(park.park_id)}
                              className={`p-2 rounded-lg font-bold ${park.is_active !== false ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-green-500 text-white hover:bg-green-600'}`}
                              title={park.is_active !== false ? 'Mark as Renovation' : 'Mark as Active'}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(park.park_id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete Park"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
          )}
        </div>

        {/* Manpower Modal */}
        {showManpowerModal && selectedPark && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
              <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Manage Manpower</h3>
                  <p className="text-sm opacity-90">{selectedPark.park_name}</p>
                </div>
                <button 
                  onClick={() => setShowManpowerModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {/* Add New Employee */}
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <h4 className="font-bold text-slate-700 mb-3">Add Employee</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Employee ID (e.g., E001)"
                      value={newEmployee.employee_id}
                      onChange={(e) => setNewEmployee({...newEmployee, employee_id: e.target.value.toUpperCase()})}
                      className="h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 text-sm"
                      data-testid="new-employee-id"
                    />
                    <input
                      type="text"
                      placeholder="Employee Name"
                      value={newEmployee.employee_name}
                      onChange={(e) => setNewEmployee({...newEmployee, employee_name: e.target.value})}
                      className="h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 text-sm"
                      data-testid="new-employee-name"
                    />
                    <select
                      value={newEmployee.category}
                      onChange={(e) => setNewEmployee({...newEmployee, category: e.target.value})}
                      className="h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 text-sm bg-white"
                      data-testid="new-employee-category"
                    >
                      <option value="Gardener">Gardener</option>
                      <option value="Sweeper">Sweeper</option>
                      <option value="Security">Security</option>
                    </select>
                  </div>
                  <button
                    onClick={addEmployee}
                    className="mt-3 h-10 px-4 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 flex items-center gap-2"
                    data-testid="add-employee-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Employee
                  </button>
                </div>

                {/* Employee List */}
                <div>
                  <h4 className="font-bold text-slate-700 mb-3">
                    Current Employees ({manpowerList.length})
                  </h4>
                  {manpowerList.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                      No employees assigned yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {manpowerList.map((emp) => (
                        <div 
                          key={emp.employee_id}
                          className="flex items-center justify-between bg-white border-2 border-slate-200 rounded-lg p-3"
                        >
                          {editingEmployee === emp.employee_id ? (
                            /* Edit Mode */
                            <div className="flex-1 flex items-center gap-2">
                              <input
                                type="text"
                                value={editEmployeeData.employee_name}
                                onChange={(e) => setEditEmployeeData({...editEmployeeData, employee_name: e.target.value})}
                                className="flex-1 h-9 px-3 rounded-lg border-2 border-blue-500 focus:border-blue-600 focus:ring-0 text-sm"
                                placeholder="Employee Name"
                                autoFocus
                              />
                              <select
                                value={editEmployeeData.category}
                                onChange={(e) => setEditEmployeeData({...editEmployeeData, category: e.target.value})}
                                className="h-9 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0 text-sm bg-white"
                              >
                                <option value="Gardener">Gardener</option>
                                <option value="Sweeper">Sweeper</option>
                                <option value="Security">Security</option>
                              </select>
                              <button
                                onClick={saveEditEmployee}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Save"
                              >
                                <CheckSquare className="w-4 h-4" />
                              </button>
                              <button
                                onClick={cancelEditEmployee}
                                className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            /* View Mode */
                            <>
                              <div>
                                <div className="font-medium text-slate-900">{emp.employee_name}</div>
                                <div className="text-sm text-slate-500">
                                  {emp.employee_id} • <span className="text-blue-600">{emp.category || 'Gardener'}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => startEditEmployee(emp)}
                                  className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                  title="Edit Employee"
                                  data-testid={`edit-employee-${emp.employee_id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => removeEmployee(emp.employee_id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  title="Delete Employee"
                                  data-testid={`remove-employee-${emp.employee_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t p-4 flex gap-3">
                <button
                  onClick={saveManpower}
                  disabled={loading}
                  className="flex-1 h-12 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
                  data-testid="save-manpower-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => setShowManpowerModal(false)}
                  className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Assign Modal */}
        {showBulkAssignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="bg-green-600 text-white p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Assign Parks to Supervisor</h3>
                  <p className="text-sm opacity-90">{selectedParkIds.length} parks selected</p>
                </div>
                <button 
                  onClick={() => setShowBulkAssignModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4">
                <label className="text-sm font-bold text-slate-700 mb-2 block">
                  Select Supervisor
                </label>
                <select
                  value={bulkAssignSupervisor}
                  onChange={(e) => setBulkAssignSupervisor(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-green-600 focus:ring-0"
                  data-testid="bulk-assign-supervisor-select"
                >
                  <option value="">-- Select Supervisor --</option>
                  {inspectors.map(sup => (
                    <option key={sup.supervisor_id} value={sup.supervisor_id}>
                      {sup.supervisor_id} - {sup.supervisor_name}
                    </option>
                  ))}
                </select>
                
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 font-medium">Selected Parks:</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedParkIds.slice(0, 5).join(', ')}
                    {selectedParkIds.length > 5 && ` +${selectedParkIds.length - 5} more`}
                  </p>
                </div>
              </div>
              
              <div className="border-t p-4 flex gap-3">
                <button
                  onClick={handleBulkAssign}
                  disabled={loading || !bulkAssignSupervisor}
                  className="flex-1 h-12 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50"
                  data-testid="confirm-bulk-assign"
                >
                  {loading ? 'Assigning...' : 'Assign Parks'}
                </button>
                <button
                  onClick={() => setShowBulkAssignModal(false)}
                  className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPasswordModal && selectedSupervisor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="bg-orange-600 text-white p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Reset Password</h3>
                  <p className="text-sm opacity-90">{selectedSupervisor.supervisor_name} ({selectedSupervisor.supervisor_id})</p>
                </div>
                <button 
                  onClick={() => setShowResetPasswordModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4">
                <label className="text-sm font-bold text-slate-700 mb-2 block">
                  New PIN (min 4 characters)
                </label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Enter new PIN"
                  className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-orange-600 focus:ring-0"
                  data-testid="new-pin-input"
                />
              </div>
              
              <div className="border-t p-4 flex gap-3">
                <button
                  onClick={handleResetPassword}
                  disabled={loading || newPin.length < 4}
                  className="flex-1 h-12 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 disabled:opacity-50"
                  data-testid="confirm-reset-password"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  onClick={() => setShowResetPasswordModal(false)}
                  className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">
                  Edit {editItem.type === 'supervisor' ? 'Supervisor' : 'Park'}
                </h3>
                <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {editItem.type === 'supervisor' ? (
                  <>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">ID (cannot edit)</label>
                      <input type="text" value={editItem.supervisor_id} disabled className="w-full h-10 px-4 rounded-lg bg-slate-100 border-2 border-slate-200" />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Name</label>
                      <input type="text" value={editData.supervisor_name || ''} onChange={(e) => setEditData({...editData, supervisor_name: e.target.value})} className="w-full h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Park ID (cannot edit)</label>
                      <input type="text" value={editItem.park_id} disabled className="w-full h-10 px-4 rounded-lg bg-slate-100 border-2 border-slate-200" />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Park Name</label>
                      <input type="text" value={editData.park_name || ''} onChange={(e) => setEditData({...editData, park_name: e.target.value})} className="w-full h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-bold text-slate-700 mb-2 block">Zone</label>
                        <input type="text" value={editData.zone || ''} onChange={(e) => setEditData({...editData, zone: e.target.value})} className="w-full h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600" />
                      </div>
                      <div>
                        <label className="text-sm font-bold text-slate-700 mb-2 block">Division</label>
                        <input type="text" value={editData.division || ''} onChange={(e) => setEditData({...editData, division: e.target.value})} className="w-full h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Assigned Supervisor</label>
                      <select value={editData.assigned_inspector_id || ''} onChange={(e) => setEditData({...editData, assigned_inspector_id: e.target.value})} className="w-full h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600">
                        <option value="">-- Select --</option>
                        {inspectors.map(sup => (
                          <option key={sup.supervisor_id} value={sup.supervisor_id}>{sup.supervisor_id} - {sup.supervisor_name}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* GPS Coordinates */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <label className="text-sm font-bold text-blue-700 mb-2 block">GPS Coordinates (for location tracking)</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-slate-600 mb-1 block">Latitude</label>
                          <input 
                            type="number" 
                            step="any"
                            value={editData.gps_lat || ''} 
                            onChange={(e) => setEditData({...editData, gps_lat: e.target.value})} 
                            placeholder="e.g., 28.6139"
                            className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 text-sm" 
                            data-testid="gps-lat-input"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 mb-1 block">Longitude</label>
                          <input 
                            type="number" 
                            step="any"
                            value={editData.gps_lng || ''} 
                            onChange={(e) => setEditData({...editData, gps_lng: e.target.value})} 
                            placeholder="e.g., 77.2090"
                            className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 text-sm" 
                            data-testid="gps-lng-input"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Google Maps Link */}
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Google Maps Link (optional)</label>
                      <input 
                        type="url" 
                        value={editData.google_maps_link || ''} 
                        onChange={(e) => setEditData({...editData, google_maps_link: e.target.value})} 
                        placeholder="https://maps.google.com/..." 
                        className="w-full h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 text-sm" 
                        data-testid="google-maps-link-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Note: GPS coordinates (Lat/Long) are used for supervisor location tracking</p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="border-t p-4 flex gap-3">
                <button onClick={handleSaveEdit} disabled={loading} className="flex-1 h-12 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setShowEditModal(false)} className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Inspector Assigned Parks Modal */}
        {showInspectorParksModal && selectedInspector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold">Assigned Parks</h3>
                    <p className="text-sm opacity-90">{selectedInspector.supervisor_name} ({selectedInspector.supervisor_id})</p>
                  </div>
                  <button
                    onClick={() => setShowInspectorParksModal(false)}
                    className="p-2 hover:bg-white/20 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[50vh]">
                {inspectorParks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No parks assigned to this supervisor</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="w-10 p-2">
                          <input
                            type="checkbox"
                            checked={selectedReassignParks.length === inspectorParks.length && inspectorParks.length > 0}
                            onChange={() => {
                              if (selectedReassignParks.length === inspectorParks.length) {
                                setSelectedReassignParks([]);
                              } else {
                                setSelectedReassignParks(inspectorParks.map(p => p.park_id));
                              }
                            }}
                            className="w-4 h-4"
                          />
                        </th>
                        <th className="text-left p-2 font-bold">Park ID</th>
                        <th className="text-left p-2 font-bold">Park Name</th>
                        <th className="text-left p-2 font-bold">Zone</th>
                        <th className="text-left p-2 font-bold">Division</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inspectorParks.map((park) => (
                        <tr 
                          key={park.park_id} 
                          className={`border-t hover:bg-slate-50 ${selectedReassignParks.includes(park.park_id) ? 'bg-blue-50' : ''}`}
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedReassignParks.includes(park.park_id)}
                              onChange={() => toggleReassignParkSelection(park.park_id)}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="p-2 font-mono text-xs">{park.park_id}</td>
                          <td className="p-2 font-medium">{park.park_name}</td>
                          <td className="p-2">{park.zone}</td>
                          <td className="p-2">{park.division || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              
              {inspectorParks.length > 0 && (
                <div className="border-t p-4 bg-slate-50">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <label className="text-sm font-bold text-slate-700 mb-1 block">Reassign to Supervisor</label>
                      <select
                        value={reassigningSupervisor}
                        onChange={(e) => setReassigningSupervisor(e.target.value)}
                        className="w-full h-10 px-3 border-2 border-slate-300 rounded-lg focus:border-blue-600"
                      >
                        <option value="">-- Select Supervisor --</option>
                        {inspectors
                          .filter(s => s.supervisor_id !== selectedInspector.supervisor_id)
                          .map(sup => (
                            <option key={sup.supervisor_id} value={sup.supervisor_id}>
                              {sup.supervisor_id} - {sup.supervisor_name}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                    <button
                      onClick={handleReassignParks}
                      disabled={reassigning || selectedReassignParks.length === 0 || !reassigningSupervisor}
                      className="h-10 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-bold"
                    >
                      {reassigning ? 'Reassigning...' : `Reassign ${selectedReassignParks.length} Park(s)`}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Select parks and choose a supervisor to reassign them.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Visit Order Combinations Modal */}
        {showVisitOrderModal && visitOrderInspector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      <Route className="w-5 h-5" />
                      Visit Order Combinations
                    </h3>
                    <p className="text-sm opacity-90">{visitOrderInspector.supervisor_name} ({visitOrderInspector.supervisor_id}) - {visitOrderParks.length} parks</p>
                  </div>
                  <button onClick={() => setShowVisitOrderModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
                {loadingVisitOrder ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-green-600 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Enable/Disable Toggle */}
                    <div className={`border-2 rounded-lg p-4 mb-4 flex items-center justify-between ${
                      visitOrderEnforced ? 'border-green-500 bg-green-50' : 'border-slate-300 bg-slate-50'
                    }`}>
                      <div>
                        <h4 className="font-bold text-slate-800 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Strict Order Enforcement
                        </h4>
                        <p className="text-sm text-slate-600 mt-1">
                          {visitOrderEnforced 
                            ? 'Supervisor must complete parks in sequence (cannot skip)' 
                            : 'Supervisor can visit parks in any order (no restriction)'}
                        </p>
                      </div>
                      <button
                        onClick={() => setVisitOrderEnforced(!visitOrderEnforced)}
                        className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                          visitOrderEnforced ? 'bg-green-600' : 'bg-slate-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                            visitOrderEnforced ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {/* Info Banner */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Auto-rotate:</strong> System rotates combinations daily (Day 1 = Combo 1, Day 2 = Combo 2, etc.)
                        <br />
                        <strong>Manual Override:</strong> Click "Use Today" to force a specific combination for today.
                      </p>
                    </div>
                    
                    {/* Active Combination Indicator */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">
                          Today's Active: Combination {activeCombinationIndex + 1}
                        </span>
                      </div>
                      <span className="text-sm text-green-600">
                        (Day {new Date().getDate()} of month)
                      </span>
                    </div>
                    
                    {/* Combinations */}
                    <div className="space-y-4">
                      {visitCombinations.map((combo, comboIndex) => (
                        <div 
                          key={comboIndex} 
                          className={`border-2 rounded-lg overflow-hidden ${
                            comboIndex === activeCombinationIndex 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-slate-200'
                          }`}
                        >
                          <div className={`px-4 py-2 flex items-center justify-between ${
                            comboIndex === activeCombinationIndex 
                              ? 'bg-green-100' 
                              : 'bg-slate-100'
                          }`}>
                            <div className="flex items-center gap-2">
                              <span className="font-bold">Combination {comboIndex + 1}</span>
                              {comboIndex === activeCombinationIndex && (
                                <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">Active Today</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {comboIndex !== activeCombinationIndex && (
                                <button
                                  onClick={() => setManualOverride(comboIndex)}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 flex items-center gap-1"
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  Use Today
                                </button>
                              )}
                              {visitCombinations.length > 1 && (
                                <button
                                  onClick={() => removeCombination(comboIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  title="Remove this combination"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="p-2">
                            {/* Add missing parks notification */}
                            {(() => {
                              const parksInCombo = new Set(combo);
                              const missingParks = visitOrderParks.filter(p => !parksInCombo.has(p.park_id));
                              if (missingParks.length > 0) {
                                return (
                                  <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-yellow-800">
                                        <AlertCircle className="w-4 h-4 inline mr-1" />
                                        {missingParks.length} park(s) not in this combination
                                      </span>
                                      <button
                                        onClick={() => {
                                          const updated = [...visitCombinations];
                                          updated[comboIndex] = [...combo, ...missingParks.map(p => p.park_id)];
                                          setVisitCombinations(updated);
                                        }}
                                        className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                                      >
                                        Add All
                                      </button>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            <div className="space-y-1">
                              {combo.map((parkId, parkIndex) => (
                                <div 
                                  key={parkId} 
                                  className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-slate-50"
                                >
                                  <span className="w-6 h-6 flex items-center justify-center bg-slate-200 text-slate-700 rounded-full text-xs font-bold">
                                    {parkIndex + 1}
                                  </span>
                                  <GripVertical className="w-4 h-4 text-slate-400" />
                                  <span className="flex-1 text-sm font-medium">{getParkName(parkId)}</span>
                                  <span className="text-xs text-slate-500">{parkId}</span>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => moveParkInCombination(comboIndex, parkIndex, 'up')}
                                      disabled={parkIndex === 0}
                                      className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => moveParkInCombination(comboIndex, parkIndex, 'down')}
                                      disabled={parkIndex === combo.length - 1}
                                      className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                                    >
                                      <ChevronDown className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add New Combination Button */}
                    {visitCombinations.length < 4 && (
                      <button
                        onClick={addNewCombination}
                        className="w-full mt-4 py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-green-500 hover:text-green-600 hover:bg-green-50 flex items-center justify-center gap-2 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                        Add Combination ({visitCombinations.length}/4)
                      </button>
                    )}
                  </>
                )}
              </div>
              
              <div className="border-t p-4 flex gap-3">
                <button 
                  onClick={saveVisitCombinations} 
                  disabled={savingVisitOrder}
                  className="flex-1 h-12 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingVisitOrder ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</>
                  ) : (
                    'Save Combinations'
                  )}
                </button>
                <button 
                  onClick={() => setShowVisitOrderModal(false)} 
                  className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataManagement;
